import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ManagedModal from './ManagedModal';

const CustomDialog = forwardRef(({ 
  title, 
  renderTitle, 
  children, 
  renderTrigger, 
  disableClose = false, 
  onClose,              
  ...rest 
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  // FIX: Dùng Ref để theo dõi trạng thái real-time, tránh Stale Closure
  const isOpenRef = useRef(false); 
  
  const [dialogId] = useState(() => 'dialog_' + Date.now() + Math.random().toString(36).slice(2, 7));

  // Đồng bộ state sang ref một cách tự động
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // --- LẮNG NGHE LỊCH SỬ TRÌNH DUYỆT ---
  const checkUrl = useCallback(() => {
    const [, hashSearch = ''] = window.location.hash.split('?');
    const searchParams = new URLSearchParams(hashSearch);
    const shouldBeOpen = searchParams.get(dialogId) === "open";

    setIsOpen((prevIsOpen) => {
      if (prevIsOpen && !shouldBeOpen) {
        if (disableClose) {
          const base = window.location.pathname + window.location.search;
          const [hashPath = ''] = window.location.hash.split('?');
          searchParams.set(dialogId, "open");
          const newHash = `${hashPath}?${searchParams.toString()}`;
          
          window.history.pushState(null, '', base + newHash);
          return true; 
        }
        
        if (onClose) onClose({}, 'popstate');
        return false;
      }
      return shouldBeOpen;
    });
  }, [dialogId, disableClose, onClose]);

  useEffect(() => {
    window.addEventListener('popstate', checkUrl);
    return () => {
      window.removeEventListener('popstate', checkUrl);
    };
  }, [checkUrl]);

  // --- HÀM MỞ ---
  const handleOpen = useCallback((callback) => {
    // Ktra bằng Ref thay vì State
    if (isOpenRef.current) {
      if (callback) callback();
      return;
    }

    console.log("[dialog] open");
    setTimeout(() => {
      isOpenRef.current = true; // Cập nhật ref ngay lập tức để khoá chốt
      setIsOpen(true);

      const base = window.location.pathname + window.location.search;
      const [hashPath = '', hashSearch = ''] = window.location.hash.split('?');
      const searchParams = new URLSearchParams(hashSearch);
      
      if (!searchParams.has(dialogId)) {
        searchParams.set(dialogId, "open");
        const newHash = `${hashPath}?${searchParams.toString()}`;
        window.history.pushState(null, '', base + newHash);
      }
      if (callback) callback();
    }, 50);
  }, [dialogId]);

  // --- HÀM ĐÓNG DÀNH CHO USER (Nút X, Cick ra ngoài) ---
  const handleUserClose = useCallback((event, reason) => {
    // FIX: Kiểm tra bằng Ref. Loại bỏ isOpen khỏi array dependencies bên dưới.
    if (!isOpenRef.current) return;

    if (disableClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    
    isOpenRef.current = false; // Cập nhật ref trước
    setIsOpen(false);
    
    if (onClose) onClose(event, reason);
    
    setTimeout(() => {
       window.history.back(); 
    }, 0);
  }, [disableClose, onClose]); // <-- Vùng nhớ hàm được giữ cố định vì không còn phụ thuộc isOpen

  // --- HÀM DEV CHỦ ĐỘNG ĐÓNG ---
  const programmaticClose = useCallback((callback) => {
    // FIX: Tương tự như trên
    if (!isOpenRef.current) {
      if (callback) callback();
      return;
    }

    isOpenRef.current = false;
    setIsOpen(false);
    
    setTimeout(() => {
      // history.back() chạy BẤT ĐỒNG BỘ: gọi callback ngay thì nơi gọi có thể pushState / mở dialog khác TRƯỚC khi
      // trình duyệt lùi xong -> lần lùi rơi vào đúng mục "dialog này đang mở" và dialog tự mở lại (lỗi Quên mật khẩu:
      // nhập OTP xong lại hiện màn hình nhập tài khoản). Nên chờ sự kiện popstate của lần lùi rồi mới gọi callback
      // (có hạn chờ phòng khi lịch sử không lùi được).
      if (callback) {
        let xong = false;
        let hen = null;
        const goi = () => {
          if (xong) return;
          xong = true;
          window.removeEventListener('popstate', goi);
          clearTimeout(hen);
          callback();
        };
        window.addEventListener('popstate', goi);
        hen = setTimeout(goi, 600);
      }
      window.history.back();
    }, 0);
  }, []);

  // Cấp quyền điều khiển ra ngoài cho Ref
  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: programmaticClose, 
    isOpen: isOpen 
  }), [handleOpen, programmaticClose, isOpen]);

  return (
    <>
      {renderTrigger && renderTrigger({ onOpen: handleOpen, isOpen })}
      
      <ManagedModal type="dialog" {...rest} open={isOpen} onClose={handleUserClose}>
        {renderTitle ? (
          renderTitle({ handleClose: handleUserClose, handleOpen })
        ) : <>
          {!!title && <DialogTitle sx={{ m: 0, p: 2,pt : rest.fullScreen?`env(safe-area-inset-top)`:undefined}}>
            {title}
            {!disableClose && (
              <IconButton
                aria-label="close"
                onClick={handleUserClose}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>}
          </>
        }
        {children}
      </ManagedModal>
    </>
  );
});

export default CustomDialog;